import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePremium } from '../../contexts/PremiumContext';
import { useTranslation } from '../../hooks/useTranslation';
import { collection, getDocs, query, where, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { createDeepeningJourney, newDeepeningJourneyId } from '../../utils/friendJourneys';
import { FRIENDSHIP_TYPES, getRelationshipIcon } from '../../utils/friendshipTypes';
import PremiumUpgradeModal from '../PremiumUpgradeModal';

const JourneysOverview = ({ onBack, onStartJourney, onViewFriendDetails }) => {
  const { currentUser } = useAuth();
  const { canStartJourney, isPremium } = usePremium();
  const { t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'active', 'completed'
  const [showStartJourneyModal, setShowStartJourneyModal] = useState(false);
  const [selectedJourneyType, setSelectedJourneyType] = useState(null);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isStartingJourney, setIsStartingJourney] = useState(false);

  // Available journey types
  const journeyTypes = [
    {
      id: 'deepening-relationships',
      title: t('reconnectAndDeepen'),
      description: t('journeyDescription'),
      icon: '💝',
      color: 'linear-gradient(45deg, #ffecd2, #fcb69f)',
      duration: `4 ${t('weeks')}`,
      activities: `17 ${t('activities')}`
    }
    // Add more journey types here in the future
  ];

  // Load friends with their journeys
  useEffect(() => {
    const loadFriendsWithJourneys = async () => {
      if (currentUser) {
        setLoading(true);
        try {
          const friendsQuery = query(
            collection(db, 'friends'),
            where('userId', '==', currentUser.uid)
          );
          const friendsSnapshot = await getDocs(friendsQuery);
          const friendsData = friendsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFriends(friendsData);
        } catch (error) {
          console.error('Error loading friends with journeys:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadFriendsWithJourneys();
  }, [currentUser]);

  // Force refresh when component mounts (in case we're navigating from another page)
  useEffect(() => {
    if (currentUser && friends.length === 0) {
      const loadFriendsWithJourneys = async () => {
        setLoading(true);
        try {
          const friendsQuery = query(
            collection(db, 'friends'),
            where('userId', '==', currentUser.uid)
          );
          const friendsSnapshot = await getDocs(friendsQuery);
          const friendsData = friendsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFriends(friendsData);
        } catch (error) {
          console.error('Error force refreshing friends with journeys:', error);
        } finally {
          setLoading(false);
        }
      };
      loadFriendsWithJourneys();
    }
  }, [currentUser, friends.length]);

  const getJourneyProgress = (journey) => {
    // Use correct totals for each week (don't rely on stored totals which may be outdated)
    const correctTotals = {
      week1: 6,
      week2: 4,
      week3: 4,
      week4: 3
    };
    
    const totalCompleted = Object.values(journey.progress).reduce((sum, week) => sum + week.completed, 0);
    // Calculate total using correct week totals instead of stored totals
    const totalActivities = Object.keys(correctTotals).reduce((sum, weekKey) => {
      return sum + (correctTotals[weekKey] || 0);
    }, 0);
    
    return {
      completed: totalCompleted,
      total: totalActivities,
      percentage: Math.round((totalCompleted / totalActivities) * 100)
    };
  };

  const getJourneyStatus = (journey) => {
    const progress = getJourneyProgress(journey);
    if (progress.percentage === 100) return 'completed';
    if (progress.percentage > 0) return 'active';
    return 'not-started';
  };

  const getAllJourneys = () => {
    const allJourneys = [];
    friends.forEach(friend => {
      if (friend.journeys && friend.journeys.length > 0) {
        friend.journeys.forEach(journey => {
          allJourneys.push({
            ...journey,
            friendId: friend.id,
            friendName: friend.name,
            friendRelationship: friend.relationship
          });
        });
      }
    });
    return allJourneys;
  };

  const getFilteredJourneys = () => {
    const allJourneys = getAllJourneys();
    
    switch (activeTab) {
      case 'active':
        return allJourneys.filter(journey => getJourneyStatus(journey) === 'active');
      case 'completed':
        return allJourneys.filter(journey => getJourneyStatus(journey) === 'completed');
      default:
        return allJourneys;
    }
  };


  const handleStartJourneyClick = () => {
    // Check if user has premium access for journeys
    if (!canStartJourney()) {
      setShowPremiumModal(true);
      return;
    }
    
    setShowStartJourneyModal(true);
    setSelectedJourneyType(null);
    setSelectedFriend(null);
  };

  const handleJourneyTypeSelect = (journeyType) => {
    setSelectedJourneyType(journeyType);
  };

  const handleFriendSelect = (friend) => {
    setSelectedFriend(friend);
  };

  const handleStartJourney = async () => {
    if (!selectedJourneyType || !selectedFriend) return;

    setIsStartingJourney(true);
    try {
      const friendRef = doc(db, 'friends', selectedFriend.id);
      const friendSnap = await getDoc(friendRef);
      const existingJourneys = friendSnap.exists() ? (friendSnap.data().journeys || []) : [];
      const journeyId = newDeepeningJourneyId(selectedJourneyType.id);
      const newJourney = createDeepeningJourney(journeyId, selectedJourneyType.id);

      await updateDoc(friendRef, {
        journeys: [...existingJourneys, newJourney]
      });

      if (onStartJourney) {
        onStartJourney(selectedFriend.id, journeyId);
      }
      setShowStartJourneyModal(false);
      setSelectedJourneyType(null);
      setSelectedFriend(null);
    } catch (error) {
      console.error('Error starting journey:', error);
    } finally {
      setIsStartingJourney(false);
    }
  };

  const handleCloseModal = () => {
    setShowStartJourneyModal(false);
    setSelectedJourneyType(null);
    setSelectedFriend(null);
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return '#1F2933'; // Dark neutral for completed
    if (status === 'active') return '#10B981'; // Green for active
    return '#3b3b3b'; // Dark grey for not-started
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return t('active');
      case 'completed': return t('completed');
      case 'not-started': return t('notStarted') || 'Not Started';
      default: return 'Unknown';
    }
  };

  const filteredJourneys = getFilteredJourneys();
  const totalJourneys = getAllJourneys().length;
  const activeJourneys = getAllJourneys().filter(j => getJourneyStatus(j) === 'active').length;
  const completedJourneys = getAllJourneys().filter(j => getJourneyStatus(j) === 'completed').length;

  if (loading) {
    return (
      <div className="journey-container">
        <div className="tracker-header">
          <button className="back-button" onClick={onBack}>
            ← {t('back')}
          </button>
          <h1>{t('reconnectAndDeepen')}</h1>
          <p>{t('journeyDescription')}</p>
          <div className="start-journey-btn">
            <button 
                className="btn btn-primary"
                onClick={handleStartJourneyClick}
              >
                🛤️ {t('startJourney')}
              </button>
              </div>
        </div>
        <div className="journey-content">
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <div className="loading-spinner">{t('loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="journey-container">
      <div className="tracker-header">
        <button className="back-button" onClick={onBack}>
          ← {t('back')}
        </button>
        <h1>{t('journeyProgress')}</h1>
        <p>{t('journeyProgress')}</p>
        <div className="start-journey-btn">
          <button 
                className="btn btn-primary"
                onClick={handleStartJourneyClick}
              >
                🛤️ {t('startJourney')}
              </button>
              </div>
      </div>
      
      <div className="journey-content">
        <div className="journey-overview">
          {/* Overview Stats */}
          <div className="overview-stats">
            <div className="stat-card">
              <div className="stat-number">{totalJourneys}</div>
              <div className="stat-label">{t('totalJourneys')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{activeJourneys}</div>
              <div className="stat-label">{t('active')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{completedJourneys}</div>
              <div className="stat-label">{t('completed')}</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="journey-tabs">
            <button 
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              {t('allJourneys')} ({totalJourneys})
            </button>
            <button 
              className={`tab-button ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              {t('active')} ({activeJourneys})
            </button>
            <button 
              className={`tab-button ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              {t('completed')} ({completedJourneys})
            </button>
          </div>

          {/* Journeys List */}
          <div className="journeys-list">
            {filteredJourneys.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🛤️</div>
                <h3>{t('noJourneysFound')}</h3>
                <p>
                  {activeTab === 'all' 
                    ? t('noJourneysYet')
                    : activeTab === 'active'
                    ? t('noActiveJourneys')
                    : t('noCompletedJourneys')
                  }
                </p>
                {activeTab === 'all' && (
                  <button 
                    className="btn btn-primary"
                    onClick={() => onBack()}
                  >
                    {t('goToFriendshipTracker')}
                  </button>
                )}
              </div>
            ) : (
              filteredJourneys.map((journey) => {
                const progress = getJourneyProgress(journey);
                const status = getJourneyStatus(journey);
                
                return (
                  <div 
                    key={`${journey.friendId}-${journey.id}`} 
                    className="journey-card clickable"
                    onClick={() => onStartJourney && onStartJourney(journey.friendId, journey.id)}
                  >
                    <div className="journey-card-layout">
                      <div className="journey-card-left">
                        <div className="journey-friend-info">
                          <div className="friend-avatar">
                            {getRelationshipIcon(journey.friendRelationship)}
                          </div>
                          <div className="friend-details">
                            <h3>{journey.friendName}</h3>
                            <p className="relationship-type">
                              {journey.friendRelationship.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                          </div>
                        </div>

                        <div className="journey-info">
                          <h4>{t('reconnectAndDeepen')}</h4>
                          <p className="journey-description">
                            {t('journeyDescription')}
                          </p>
                        </div>
                      </div>

                      <div className="journey-card-right">
                        <div className="journey-status">
                          <div 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(status) }}
                          >
                            {getStatusText(status)}
                          </div>
                        </div>

                        <div className="journey-meta">
                          <span className="meta-item">
                            Started: {new Date(journey.startDate).toLocaleDateString()}
                          </span>
                          <span className="meta-item">
                            Progress: {progress.completed} of {progress.total} activities
                          </span>
                        </div>

                        <div className="journey-progress">
                          <div className="progress-header">
                            <span className="progress-label">Overall Progress</span>
                            <span className="progress-percentage">{progress.percentage}%</span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${progress.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h3>{t('quickActions')}</h3>
            <div className="action-buttons">
              <button 
                className="btn btn-secondary"
                onClick={() => onBack()}
              >
                📊 {t('goToFriendshipTrackerButton')}
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleStartJourneyClick}
              >
                🛤️ {t('startNewJourneyModal')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Start Journey Modal */}
      {showStartJourneyModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content start-journey-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('startNewJourneyModal')}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Step 1: Select Journey Type */}
              <div className="journey-selection-step">
                <h4>{t('step1ChooseJourneyType')}</h4>
                <div className="journey-types-grid">
                  {journeyTypes.map((journeyType) => (
                    <div
                      key={journeyType.id}
                      className={`journey-type-card ${selectedJourneyType?.id === journeyType.id ? 'selected' : ''}`}
                      onClick={() => handleJourneyTypeSelect(journeyType)}
                    >
                      <div 
                        className="journey-type-icon"
                        style={{ background: journeyType.color }}
                      >
                        <span>{journeyType.icon}</span>
                      </div>
                      <div className="journey-type-info">
                        <h5>{journeyType.title}</h5>
                        <p className="journey-type-description">{journeyType.description}</p>
                        <div className="journey-type-meta">
                          <span className="meta-item">⏱️ {journeyType.duration}</span>
                          <span className="meta-item">📋 {journeyType.activities}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 2: Select Friend */}
              {selectedJourneyType && (
                <div className="friend-selection-step">
                  <h4>{t('step2ChooseFriend')}</h4>
                  {friends.length === 0 ? (
                    <div className="no-friends-message">
                      <p>{t('noFriendsForJourneyMessage')}</p>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => {
                          handleCloseModal();
                          onBack(); // Go to Friendship Tracker
                        }}
                      >
                        {t('goToFriendshipTrackerButton')}
                      </button>
                    </div>
                  ) : (
                    <div className="friends-selection-grid">
                      {friends.map((friend) => (
                        <div
                          key={friend.id}
                          className={`friend-selection-card ${selectedFriend?.id === friend.id ? 'selected' : ''}`}
                          onClick={() => handleFriendSelect(friend)}
                        >
                          <div className="friend-avatar">
                            {getRelationshipIcon(friend.relationship)}
                          </div>
                          <div className="friend-info">
                            <h5>{friend.name}</h5>
                            <p className="relationship-type">
                              {friend.relationship.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                            {friend.journeys && friend.journeys.length > 0 && (
                              <p className="existing-journeys">
                                {friend.journeys.length} {friend.journeys.length > 1 ? t('existingJourneys') : t('existingJourney')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                {t('cancel')}
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleStartJourney}
                disabled={!selectedJourneyType || !selectedFriend || isStartingJourney}
              >
                {isStartingJourney ? t('loading') : t('startJourney')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal 
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        reason="journey_access"
      />
    </div>
  );
};

export default JourneysOverview;
